import { useId, useState } from 'react';
import { ArrowRight, Scale, TriangleAlert } from 'lucide-react';
import { Input } from '@sara/ui/components/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@sara/ui/components/dialog';
import { cn } from '@sara/ui/lib/utils';
import { MarkdownText } from '../../../../components/markdown-text';
import {
  DEFAULT_CONSENT_STATEMENT,
  DEFAULT_CONSENT_DETAILS,
  DEFAULT_SECONDARY_LINK_LABEL,
  DEFAULT_SECONDARY_LINK_DESCRIPTION,
  isDefaultConsentWithHiddenLink,
  resolveConsentPreview,
  type ConsentConfigFormValue,
  type ConsentPreviewSection,
  type LinkMode,
} from '../../lib/consent-fields';

interface CandidateConsentFieldsProps {
  value: ConsentConfigFormValue;
  onChange: (next: ConsentConfigFormValue) => void;
  disabled?: boolean;
}

const subLabelClassName = 'text-[12px] font-medium text-foreground/80';
const helpClassName = 'text-[12.5px] leading-snug text-muted-foreground';
const textareaClassName =
  'flex w-full rounded-md border border-border/70 bg-background px-3 py-2.5 font-mono text-[12.5px] leading-relaxed text-foreground/90 ring-offset-background placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

/** A small text-button used for the inline mode toggles ("Custom", "Hide", …). */
function ModeLink({
  active,
  onClick,
  disabled,
  children,
}: {
  active?: boolean;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      className={cn(
        'text-[11.5px] font-medium underline-offset-2 transition-colors disabled:opacity-50',
        active ? 'text-foreground' : 'text-primary hover:underline'
      )}
    >
      {children}
    </button>
  );
}

/**
 * The per-agent consent + secondary-link editor for the Candidate experience
 * card. Controlled (value + onChange), so both the create and new-version forms
 * wire it through a single composite `consentConfig` field. Each part is
 * default / custom (the link can also be hidden); switching to custom seeds the
 * platform default text so editing starts from the real copy without freezing
 * it. A live preview renders what the candidate will see.
 */
export function CandidateConsentFields({ value, onChange, disabled }: CandidateConsentFieldsProps) {
  const ids = useId();
  const set = (patch: Partial<ConsentConfigFormValue>) => onChange({ ...value, ...patch });

  const statementCustom = value.statementMode === 'custom';
  const detailsCustom = value.detailsMode === 'custom';
  const statementError =
    statementCustom && value.statementText.trim().length === 0
      ? 'Consent statement is required when customized.'
      : null;
  const detailsError =
    detailsCustom && value.detailsText.trim().length === 0
      ? 'Consent details are required when customized.'
      : null;
  const labelError =
    value.labelMode === 'custom' && value.labelText.trim().length === 0
      ? 'Link label is required when customized.'
      : null;
  const descriptionError =
    value.descriptionMode === 'custom' && value.descriptionText.trim().length === 0
      ? 'Description is required when customized.'
      : null;

  const preview = resolveConsentPreview(value);
  const showWarning = isDefaultConsentWithHiddenLink(value);

  return (
    <div className="rounded-md border border-border/60 bg-muted/20 p-4">
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        {/* LEFT — the controls */}
        <div className="min-w-0 space-y-6">
          {/* Consent group */}
          <div className="space-y-5">
            {/* Statement */}
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <label htmlFor={`${ids}-statement`} className={subLabelClassName}>
                  Consent statement
                </label>
                {statementCustom ? (
                  <ModeLink onClick={() => set({ statementMode: 'default' })} disabled={disabled}>
                    Use platform default
                  </ModeLink>
                ) : (
                  <ModeLink
                    onClick={() =>
                      set({
                        statementMode: 'custom',
                        statementText: value.statementText || DEFAULT_CONSENT_STATEMENT,
                      })
                    }
                    disabled={disabled}
                  >
                    Customize
                  </ModeLink>
                )}
              </div>
              {statementCustom ? (
                <>
                  <textarea
                    id={`${ids}-statement`}
                    value={value.statementText}
                    onChange={(e) => set({ statementText: e.target.value })}
                    disabled={disabled}
                    rows={4}
                    className={textareaClassName}
                    aria-invalid={statementError ? true : undefined}
                    autoComplete="off"
                  />
                  {statementError && (
                    <p className="text-[12px] text-destructive">{statementError}</p>
                  )}
                </>
              ) : (
                <p className={cn(helpClassName, 'italic')}>
                  Using the platform default (current legal-reviewed consent version).
                </p>
              )}
              <p className={helpClassName}>
                The short statement next to the checkbox. Supports <code>**bold**</code>,{' '}
                <code>*italics*</code>, and line breaks (rendered inline — not lists or paragraphs).
              </p>
            </div>

            {/* Details */}
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <label htmlFor={`${ids}-details`} className={subLabelClassName}>
                  &ldquo;Learn more&rdquo; details
                </label>
                {detailsCustom ? (
                  <ModeLink onClick={() => set({ detailsMode: 'default' })} disabled={disabled}>
                    Use platform default
                  </ModeLink>
                ) : (
                  <ModeLink
                    onClick={() =>
                      set({
                        detailsMode: 'custom',
                        detailsText: value.detailsText || DEFAULT_CONSENT_DETAILS,
                      })
                    }
                    disabled={disabled}
                  >
                    Customize
                  </ModeLink>
                )}
              </div>
              {detailsCustom ? (
                <>
                  <textarea
                    id={`${ids}-details`}
                    value={value.detailsText}
                    onChange={(e) => set({ detailsText: e.target.value })}
                    disabled={disabled}
                    rows={8}
                    className={textareaClassName}
                    aria-invalid={detailsError ? true : undefined}
                    autoComplete="off"
                  />
                  {detailsError && <p className="text-[12px] text-destructive">{detailsError}</p>}
                </>
              ) : (
                <p className={cn(helpClassName, 'italic')}>Using the platform default sections.</p>
              )}
              <p className={helpClassName}>
                The long disclosure behind &ldquo;Learn more&rdquo;. Lines starting with{' '}
                <code>## </code> become section headings.
              </p>
            </div>

            {/* Legal note */}
            <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[11.5px] leading-snug text-amber-700 dark:text-amber-400">
              <Scale className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={1.75} />
              <span>
                The platform default is legal-reviewed. Custom consent should be reviewed by legal
                before this agent is used with pros.
              </span>
            </div>
          </div>

          {/* Secondary link group */}
          <div className="space-y-4 border-t border-border/60 pt-5">
            <div className="space-y-1">
              <p className={subLabelClassName}>Secondary link under &ldquo;Start call&rdquo;</p>
              <p className={helpClassName}>
                Each part is default, custom, or hidden. Clicking the link records a decline and
                shows your declined message.
              </p>
            </div>

            <LinkPartField
              id={`${ids}-label`}
              label="Link label"
              mode={value.labelMode}
              text={value.labelText}
              defaultText={DEFAULT_SECONDARY_LINK_LABEL}
              seedDefault={DEFAULT_SECONDARY_LINK_LABEL}
              placeholder="Request a live call instead"
              error={labelError}
              disabled={disabled}
              onModeChange={(mode, seed) => set({ labelMode: mode, labelText: seed })}
              onTextChange={(labelText) => set({ labelText })}
            />

            <LinkPartField
              id={`${ids}-description`}
              label="Description"
              mode={value.descriptionMode}
              text={value.descriptionText}
              defaultText="the platform accommodation note"
              seedDefault={DEFAULT_SECONDARY_LINK_DESCRIPTION}
              placeholder="Can't do the call right now? We'll call you before your shift."
              error={descriptionError}
              disabled={disabled}
              onModeChange={(mode, seed) => set({ descriptionMode: mode, descriptionText: seed })}
              onTextChange={(descriptionText) => set({ descriptionText })}
            />
          </div>

          {showWarning && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-[11.5px] leading-snug text-destructive">
              <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={1.75} />
              <span>
                The platform-default consent text promises a live-call pathway, but the link is
                hidden. Consider a custom consent statement or keeping the link visible.
              </span>
            </div>
          )}
        </div>

        {/* RIGHT — a non-interactive preview of the candidate's view */}
        <div className="space-y-2 border-t border-border/60 pt-5 lg:sticky lg:top-4 lg:self-start lg:border-t-0 lg:border-l lg:border-border/60 lg:pt-0 lg:pl-6">
          <p className={subLabelClassName}>Candidate preview</p>
          <p className={helpClassName}>
            A non-interactive preview of what the candidate sees — updates as you type.
          </p>
          <ConsentPreview
            statement={preview.statement}
            sections={preview.sections}
            label={preview.label}
            description={preview.description}
          />
        </div>
      </div>
    </div>
  );
}

function LinkPartField({
  id,
  label,
  mode,
  text,
  defaultText,
  seedDefault,
  placeholder,
  error,
  disabled,
  onModeChange,
  onTextChange,
}: {
  id: string;
  label: string;
  mode: LinkMode;
  text: string;
  /** Short human label describing the default, shown in the "Using …" hint. */
  defaultText: string;
  /** The real platform-default text used to seed the editor on "Custom". */
  seedDefault: string;
  placeholder: string;
  error: string | null;
  disabled?: boolean;
  onModeChange: (mode: LinkMode, seedText: string) => void;
  onTextChange: (text: string) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <label htmlFor={id} className="text-[12px] font-medium text-muted-foreground">
          {label}
        </label>
        <div className="flex items-center gap-2.5">
          <ModeLink
            active={mode === 'default'}
            onClick={() => onModeChange('default', text)}
            disabled={disabled}
          >
            Default
          </ModeLink>
          <span className="text-muted-foreground/40">·</span>
          <ModeLink
            active={mode === 'custom'}
            onClick={() => onModeChange('custom', text || seedDefault)}
            disabled={disabled}
          >
            Custom
          </ModeLink>
          <span className="text-muted-foreground/40">·</span>
          <ModeLink
            active={mode === 'hidden'}
            onClick={() => onModeChange('hidden', text)}
            disabled={disabled}
          >
            Hide
          </ModeLink>
        </div>
      </div>
      {mode === 'custom' ? (
        <>
          <Input
            id={id}
            value={text}
            onChange={(e) => onTextChange(e.target.value)}
            disabled={disabled}
            placeholder={placeholder}
            aria-invalid={error ? true : undefined}
            autoComplete="off"
            className="h-9"
          />
          {error && <p className="text-[12px] text-destructive">{error}</p>}
        </>
      ) : (
        <p className="text-[12.5px] italic leading-snug text-muted-foreground">
          {mode === 'hidden' ? 'Hidden — not shown to the candidate.' : `Using ${defaultText}.`}
        </p>
      )}
    </div>
  );
}

/**
 * An approximation of the candidate lobby's consent card + Start-call CTA + link
 * slot, using the same constrained markdown rules as the interview app. Not
 * pixel-exact — close enough to catch wording and formatting. The card is a
 * non-interactive preview EXCEPT "Learn more", which opens the same disclosure
 * dialog the candidate would see so the author can preview both layers.
 */
function ConsentPreview({
  statement,
  sections,
  label,
  description,
}: {
  statement: string;
  sections: ConsentPreviewSection[];
  label: string | null;
  description: string | null;
}) {
  const [learnMoreOpen, setLearnMoreOpen] = useState(false);
  return (
    <div className="mx-auto flex max-w-sm flex-col gap-3 rounded-xl border border-border/70 bg-background p-4">
      <div className="flex items-start gap-2.5 rounded-lg border border-border/70 bg-card px-3 py-2.5">
        <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded bg-primary text-[10px] text-primary-foreground">
          ✓
        </span>
        <div className="space-y-1">
          <MarkdownText inline className="text-[11.5px] leading-relaxed text-foreground">
            {statement}
          </MarkdownText>
          <button
            type="button"
            onClick={() => setLearnMoreOpen(true)}
            className="block text-[11.5px] text-primary underline-offset-2 hover:underline"
          >
            Learn more
          </button>
        </div>
      </div>
      {/* Non-interactive lobby chrome (visual only). */}
      <div className="pointer-events-none select-none space-y-3">
        <div className="flex items-center justify-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-[13px] font-semibold text-primary-foreground">
          Start call <ArrowRight className="h-3.5 w-3.5" />
        </div>
        <p className="text-center text-[11px] text-muted-foreground">You can stop at any time.</p>
        {(label || description) && (
          <div className="flex flex-col items-center gap-1 pt-1 text-center">
            {label && (
              <span className="text-[12px] font-medium text-foreground underline underline-offset-4">
                {label}
              </span>
            )}
            {description && (
              <p className="max-w-[280px] text-[10.5px] leading-snug text-muted-foreground">
                {description}
              </p>
            )}
          </div>
        )}
      </div>

      <Dialog open={learnMoreOpen} onOpenChange={setLearnMoreOpen}>
        <DialogContent
          aria-describedby={undefined}
          className="mx-4 max-h-[85vh] max-w-[600px] overflow-y-auto sm:mx-0"
        >
          <DialogHeader>
            <DialogTitle>How this conversation works</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {sections.map((section) => (
              <section key={`${section.heading} ${section.body}`}>
                {section.heading && (
                  <h3 className="text-sm font-semibold text-foreground">{section.heading}</h3>
                )}
                <MarkdownText className="mt-1 text-sm text-muted-foreground">
                  {section.body}
                </MarkdownText>
              </section>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
